import React from "react";
import { isEmpty, pick, keys } from "lodash";
import DiscountForm from "./DiscountForm";
import {
  compose,
  withHandlers,
  setDisplayName,
  lifecycle,
  branch,
  withProps,
  renderNothing,
  renderComponent
} from "recompose";
import { connect } from "react-redux";
import { actions } from "../modules/discounts";
import { guid } from "../helpers/index";
import DiscountList from "./DiscountList";
import {
  withFetchContractActions,
  withState as withContractSte
} from "../containers/WithFetchContract/index";
import { withRouter } from "react-router-dom";
import Button from "./CustomButton";
import FontAwesomeIcon from "@fortawesome/react-fontawesome";
import { faEdit, faTrash } from "@fortawesome/fontawesome-free-solid";
import WithTotals from "./WithTotals";

const mapStateToProps = state => ({
  submittingDiscount: state.discounts.submittingDiscount,
  failSubmitDiscount: state.discounts.failSubmitDiscount,
  successSubmitDiscount: state.discounts.successSubmitDiscount,
  currentDealer: state.dealers.currentDealer,
  currentContract: state.contracts.currentContract,
  currentDiscount: state.discounts.currentDiscount,
  discounts: state.discounts.discounts,
  role: state.settings.role
});

const mapDispatchToProps = {
  createDiscountAsync: actions.createDiscountAsync,
  createDiscountSync: actions.createDiscountSync,
  updateDiscountSync: actions.updateDiscountSync,
  updateDiscountAsync: actions.updateDiscountAsync,
  setCurrentDiscount: actions.setCurrentDiscount,
  deleteDiscountSync: actions.deleteDiscountSync,
  deleteDiscountAsync: actions.deleteDiscountAsync,
  resetState: actions.resetState
};

const withDiscountState = connect(
  mapStateToProps,
  mapDispatchToProps
);

const withHandleSubmit = compose(
  withContractSte,
  withRouter,
  withFetchContractActions,
  WithTotals,
  withHandlers({
    onSubmit: ({
      fetchContractSeamless,
      createDiscountAsync,
      createDiscountSync,
      updateDiscountAsync,
      updateDiscountSync,
      currentContract,
      currentDiscount
    }) => (data, { resetForm }) => {
      const clearForm = () => {
        resetForm({ quantity: "", discount_type: "", description: "" });
      };
      if (currentDiscount) {
        if (currentContract) {
          updateDiscountAsync({
            data,
            dealer_id: currentContract.dealer.id,
            contract_id: currentContract.id,
            discount_id: currentDiscount.id
          }).then(clearForm);
        } else {
          updateDiscountSync(data);
          clearForm();
        }
      } else {
        if (currentContract) {
          createDiscountAsync({
            data,
            dealer_id: currentContract.dealer.id,
            contract_id: currentContract.id
          }).then(resolvedDiscount => {
            const properState = ["waiting_for_service", "in_service"].includes(
              currentContract.aasm_state
            );
            if (properState) {
              fetchContractSeamless();
            }
            clearForm();
          });
        } else {
          createDiscountSync(guid(), data);
          clearForm();
        }
      }
    },
    clearDiscount: ({ setCurrentDiscount }) => ev => {
      setCurrentDiscount(null);
    }
  })
);

const EmptyDiscounts = () => (
  <h4 className="text-muted">There are no discounts.</h4>
);

const DiscountsEnhacer = compose(
  setDisplayName("Discounts"),
  withDiscountState,
  branch(
    ({ discounts }) => isEmpty(discounts),
    renderComponent(EmptyDiscounts)
  ),
  withHandlers({
    onClickUpdate: ({ setCurrentDiscount }) => discount => () => {
      setCurrentDiscount(discount);
    },
    onClickDelete: ({
      currentContract,
      deleteDiscountAsync,
      deleteDiscountSync
    }) => discount => () => {
      if (window.confirm("Do you really want to remove this discount?")) {
        if (currentContract) {
          deleteDiscountAsync({
            dealer_id: currentContract.dealer.id,
            contract_id: currentContract.id,
            discount_id: discount.id
          });
        } else {
          deleteDiscountSync(discount.id);
        }
      }
    }
  })
);

const Discounts = DiscountsEnhacer(
  ({
    role,
    discounts,
    onClickDelete,
    onClickUpdate,
    submittingDiscount,
    currentContract
  }) => (
    <DiscountList discounts={discounts || []}>
      {({ discount }) => {
        if (
          !(
            !currentContract ||
            (![
              "pending_diagnostic",
              "waiting_for_payment",
              "waiting_for_invoice",
              "waiting_for_pickup",
              "completed"
            ].includes(currentContract.aasm_state) && role) === "staff"
          )
        ) {
          return null;
        }
        return (
          <div className="row">
            <Button
              simple
              color="info"
              disabled={submittingDiscount}
              onClick={onClickUpdate(discount)}
            >
              <FontAwesomeIcon icon={faEdit} />
            </Button>
            <Button
              simple
              color="info"
              disabled={submittingDiscount}
              onClick={onClickDelete(discount)}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </div>
        );
      }}
    </DiscountList>
  )
);

const withFormProps = withProps(({ currentDiscount }) => ({
  initialValues: discountInitialValues(currentDiscount)
}));

const EnhancedForm = compose(
  withDiscountState,
  branch(
    ({ currentContract, role }) =>
      !(
        !currentContract ||
        (![
          "pending_diagnostic",
          "waiting_for_payment",
          "waiting_for_invoice",
          "waiting_for_pickup",
          "completed"
        ].includes(currentContract.aasm_state) && role) === "staff"
      ),
    renderNothing
  ),
  withHandleSubmit,
  withFormProps
)(DiscountForm);

const withLifeCycle = lifecycle({
  componentWillUnmount() {
    this.props.resetState();
  }
});

const enhanced = compose(
  setDisplayName("DiscountSection"),
  withDiscountState,
  withProps(({ currentDealer, currentContract, values }) => ({
    currentContract: currentContract
      ? currentContract
      : Object.assign({}, values, { dealer: currentDealer })
  })),
  withLifeCycle
);

const DiscountSection = enhanced(({ title, discounts }) => (
  <div className="col">
    {title && <h4>{title}</h4>}
    <EnhancedForm />
    <Discounts />
  </div>
));

const discountInitialValues = currentDiscount => {
  let initialValues = { quantity: "", discount_type: "", description: "" };
  return isEmpty(currentDiscount)
    ? initialValues
    : pick(currentDiscount, keys(initialValues));
};

export default DiscountSection;
